<script setup lang="ts">
import { Accordion } from "~/components/clarity-ui/Accordion";
import { AccordionDetails } from "~/components/clarity-ui/Accordion/AccordionDetails";
import { AccordionSummary } from "~/components/clarity-ui/Accordion/AccordionSummary";
import { Heading } from "~/components/clarity-ui/Heading";
import useMagnoliaSite from "~/composables/useMagnoliaSite";
import useFaqTracking from "~/composables/tracking/useFaqTracking";
import DownloadLinkComponent from "~/templates/components/cms/DownloadLinkComponent.vue";
import RichTextComponent from "~/templates/components/cms/RichTextComponent.vue";
import VideoPictureElement from "~/templates/elements/VideoPictureElement.vue";
import { DIMENSIONS } from "~/utils/helper/imaging";
import { containsImage, isResizerImage, mapNodes } from "~/utils/helper/magnolia";
import { contentToTailwindClasses } from "~/utils/helper/tailwind";

export interface FaqProps {
  alignment?: string;
  faqs?: object[];
  headline?: string;
  richText?: never;
  subCategory?: string[];
}

const { alignment, faqs, headline, richText, subCategory } = defineProps<FaqProps>();

const imageBreakpoints = {
  start: DIMENSIONS["start"].ratio["3/2"],
  sm: DIMENSIONS["sm"].ratio["3/2"],
  md: DIMENSIONS["md"].width["1/2"].ratio["3/2"],
  xl: DIMENSIONS["xl"].width["1/2"].ratio["3/2"],
};

const { trackAccordionExpand } = useFaqTracking();
const { mgnlContentPath } = useMagnoliaContent();
const { currentLanguage } = useMagnoliaLanguage();

const categoryFAQs = ref([]);

const faqList = computed(() => {
  const combinedFaqs = [...(categoryFAQs?.value ?? []), ...(faqs ?? [])];
  const flatFaqs = combinedFaqs
    ?.map((faq) => (faq?.["@nodeType"] === "mgnl:folder" ? mapNodes(faq) : faq))
    ?.flat();

  if (
    mgnlContentPath.value.startsWith("/SourceMarket") ||
    mgnlContentPath.value.startsWith("/MasterContent")
  ) {
    // skip filter for source and master
    return flatFaqs;
  } else {
    // filter by market visibility
    return flatFaqs?.filter((faq) => {
      return !(
        faq?.["showInAllMarkets"] === "false" &&
        faq?.["showInTheseMarkets"]?.some((market) => mgnlContentPath.value.startsWith(market)) ===
          false
      );
    });
  }
});

const imageFaqList = computed(() => faqList.value.filter((faq) => containsImage(faq?.image)));

const {
  public: { mgnlApi },
} = useRuntimeConfig();

const { mgnlSiteHeaders } = useMagnoliaSite();

// create key based on category configuration
const key = `faq-subcategory-${subCategory?.join("|")}`;

const handleToggle = (e, question) => {
  if (e?.target?.open) {
    trackAccordionExpand(question);
  }
};

const { data, refresh } = await useAsyncData(
  key,
  async () => {
    try {
      const params = new URLSearchParams({
        lang: currentLanguage.value,
        orderBy: "priority,@name",
        subCategory: subCategory.join("|"),
      });
      return $fetch(`${mgnlApi}/.rest/delivery/faq/v1?${params.toString()}`, {
        method: "GET",
        headers: mgnlSiteHeaders,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return {};
    }
  },
  { immediate: false },
);

if (subCategory?.filter((entry) => entry?.length > 0)?.length > 0) {
  await refresh();
  // add category values
  categoryFAQs.value = data?.value?.results ?? [];
}
</script>

<template>
  <div class="faq-component">
    <Heading
      v-if="headline"
      :class="contentToTailwindClasses({ textAlign: alignment })"
      class="mb-8"
      size="large"
      >{{ headline }}</Heading
    >
    <RichTextComponent v-if="richText" class="mb-5" :rich-text="richText" />
    <Accordion divider>
      <template v-for="(faq, index) in faqList" :key="faq">
        <AccordionDetails
          :id="faq?.anchor ?? faq?.['@name'] ?? `faq-${index}`"
          :update-hash="true"
          itemprop="mainEntity"
          itemscope
          itemtype="https://schema.org/Question"
          @toggle="(e) => handleToggle(e, faq?.question)"
        >
          <AccordionSummary itemprop="text">{{ faq?.question }}</AccordionSummary>
          <div
            :class="{ 'md:grid-cols-2': isResizerImage(faq?.image) }"
            class="grid gap-4 xl:gap-6"
            itemprop="acceptedAnswer"
            itemscope
            itemtype="https://schema.org/Answer"
          >
            <VideoPictureElement
              v-if="isResizerImage(faq?.image)"
              :breakpoints="imageBreakpoints"
              :class="[
                {
                  'order-last': faq?.mediaPositionMobile === 'right',
                  'md:order-first': faq?.mediaPosition === 'left',
                },
                imageFaqList?.indexOf(faq) % 2 !== 0 || faq?.mediaPosition === 'right'
                  ? 'md:order-last'
                  : 'md:order-none',
              ]"
              :img="faq?.image"
              :show-video-inline="faq?.showVideoInline"
              :video="faq?.video"
              class="overflow-hidden rounded-xl"
            />
            <RichTextComponent v-if="faq?.answer" :rich-text="faq?.answer" itemprop="text" />
          </div>
          <div v-if="Object.keys(faq?.download ?? {}).length > 0" class="flex flex-col gap-4">
            <DownloadLinkComponent
              v-for="downloadObject in faq?.download"
              :key="downloadObject['id']"
              :download="downloadObject"
            />
          </div>
        </AccordionDetails>
      </template>
    </Accordion>
  </div>
</template>
